import { useCallback } from 'react';
import { PackBySlugOrGuid, PaginatedPackSamples } from 'apollo/pack/pack.gql';
import {
    RepackBySlugOrGuid,
    PaginatedRepackSamples,
} from 'apollo/repack/repack.gql';
import { useLazyQuery } from '@apollo/client';
import {
    PackBySlugOrGuidQuery,
    PackBySlugOrGuidQueryVariables,
    PackType,
    PaginatedPackSamplesQuery,
    PaginatedPackSamplesQueryVariables,
    PaginatedRepackSamplesQuery,
    PaginatedRepackSamplesQueryVariables,
    RepackBySlugOrGuidQuery,
    RepackBySlugOrGuidQueryVariables,
} from 'types/generated/graphql';

const useOptimisticQuery = () => {
    const [getPack] = useLazyQuery<
        PackBySlugOrGuidQuery,
        PackBySlugOrGuidQueryVariables
    >(PackBySlugOrGuid);
    const [getPackSamples] = useLazyQuery<
        PaginatedPackSamplesQuery,
        PaginatedPackSamplesQueryVariables
    >(PaginatedPackSamples);

    const [getRepack] = useLazyQuery<
        RepackBySlugOrGuidQuery,
        RepackBySlugOrGuidQueryVariables
    >(RepackBySlugOrGuid);
    const [getRepackSamples] = useLazyQuery<
        PaginatedRepackSamplesQuery,
        PaginatedRepackSamplesQueryVariables
    >(PaginatedRepackSamples);

    const optimisticPreviewQuery = useCallback(
        ({
            guid,
            type,
            signal,
        }: {
            guid: string;
            type: PackType;
            signal: AbortSignal;
        }) => {
            if (!guid) {
                return;
            }

            switch (type) {
                case PackType.Pack:
                    getPack({
                        variables: { packSlugOrGuid: guid },
                        context: {
                            fetchOptions: {
                                signal: signal,
                            },
                        },
                    });
                    getPackSamples({
                        variables: { packGuid: guid, take: 8 },
                        context: {
                            fetchOptions: {
                                signal: signal,
                            },
                        },
                    });
                    break;

                case PackType.Repack:
                    getRepack({
                        variables: { repackSlugOrGuid: guid },
                        context: {
                            fetchOptions: {
                                signal: signal,
                            },
                        },
                    });
                    getRepackSamples({
                        variables: { repackGuid: guid, take: 8 },
                        context: {
                            fetchOptions: {
                                signal: signal,
                            },
                        },
                    });
                    break;
            }
        },
        [getPack, getPackSamples, getRepack, getRepackSamples],
    );

    return optimisticPreviewQuery;
};

export default useOptimisticQuery;
